// The application frame (UX-100, UX-008, UXD-04, UXD-25): left rail with Home, Assets, Demos,
// Insights and, for admins only, Settings; "Send a demo" as the one standing action; a top bar
// with the company name, "Switch company" for people in more than one company, and the avatar
// menu for personal settings and sign-out.

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router";

import { api, seesSettings, type Me } from "../api";
import { formatDate, t, type MessageKey } from "../i18n";
import { useSignOut, useSwitchCompany } from "../session";
import { Icon, type IconName } from "./icons";
import { FeedbackDialog } from "./FeedbackDialog";
import { NotificationBell } from "./Notifications";
import { Button, Logo } from "./ui";

const NAV: { to: string; key: MessageKey; icon: IconName; adminOnly?: boolean }[] = [
  { to: "/", key: "nav.home", icon: "home" },
  { to: "/assets", key: "nav.assets", icon: "assets" },
  { to: "/demos", key: "nav.demos", icon: "demos" },
  { to: "/insights", key: "nav.insights", icon: "insights" },
  { to: "/settings", key: "nav.settings", icon: "settings", adminOnly: true },
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function Shell({ me }: { me: Me }) {
  const active = me.companies.find((c) => c.id === me.active_company_id);
  const admin = seesSettings(me);
  const navigate = useNavigate();
  // Below the tablet breakpoint the rail is a drawer opened from the top bar.
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();
  useEffect(() => setDrawer(false), [location.pathname]);

  return (
    <div className="flex min-h-full">
      {drawer && <div className="fixed inset-0 z-30 bg-ink/40 md:hidden" onClick={() => setDrawer(false)} aria-hidden />}
      <aside
        className={`${drawer ? "fixed inset-y-0 left-0 z-40 flex bg-ground shadow-lg" : "hidden"} w-[224px] shrink-0 flex-col gap-1.5 border-r border-rule px-3.5 py-[18px] md:static md:flex md:shadow-none`}
      >
        <div className="mb-4 px-2">
          <Logo className="h-[22px]" />
        </div>
        <nav aria-label={t("nav.main")} className="flex flex-col gap-0.5">
          {NAV.filter((item) => !item.adminOnly || admin).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-[7px] px-2.5 py-2 font-normal no-underline ${
                  isActive ? "bg-panel text-ink" : "text-steel hover:text-ink"
                }`
              }
            >
              <Icon name={item.icon} />
              {t(item.key)}
            </NavLink>
          ))}
        </nav>
        <div className="mt-3">
          <Button kind="primary" className="w-full" onClick={() => navigate("/demos/new")}>
            {t("nav.send")}
          </Button>
        </div>
        <div className="mt-auto flex flex-col gap-1 px-1">
          <NavLink to="/help" className="text-[13px] text-steel no-underline hover:text-ink">
            {t("help.title")}
          </NavLink>
          <div className="text-[11px] text-steel">{t("brand.version", { version: "0.1" })}</div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex min-h-[58px] items-center gap-3 border-b border-rule-soft px-4 py-3 md:px-6">
          <button
            type="button"
            className="-ml-1 rounded-[6px] border-0 bg-transparent p-1.5 text-steel hover:text-ink md:hidden"
            aria-label={t("nav.main")}
            aria-expanded={drawer}
            onClick={() => setDrawer(true)}
          >
            <Icon name="menu" />
          </button>
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="grid h-[26px] w-[26px] place-items-center rounded-[6px] bg-[#16181d] text-[11px] font-bold text-[#7a99f5]">
              {initials(active?.name ?? "?")}
            </span>
            <span className="truncate font-medium">{active?.name}</span>
            {me.companies.length > 1 && <CompanySwitcher me={me} />}
          </div>
          <span className="flex-1" />
          <SearchBox />
          <NotificationBell />
          <AccountMenu me={me} />
        </header>
        {!me.email_verified && <VerifyBanner />}
        {me.support && <SupportBanner support={me.support} />}
        <main className="flex flex-1 flex-col">
          {!me.active_company_id && me.companies.length > 0 && !me.support ? (
            <ChooseCompany me={me} />
          ) : (
            <Suspense fallback={<div className="p-6 text-sm text-steel">{t("common.working")}</div>}>
              <Outlet />
            </Suspense>
          )}
        </main>
      </div>
    </div>
  );
}

/// A session without a company (it was deleted, or the person left it) asks which one to open.
function ChooseCompany({ me }: { me: Me }) {
  const switchCompany = useSwitchCompany();
  return (
    <div className="mx-auto mt-16 grid w-[min(440px,calc(100%-32px))] gap-3">
      <h1 className="m-0 text-xl font-semibold">{t("company.choose")}</h1>
      <p className="m-0 text-sm text-steel">{t("company.chooseLead")}</p>
      {me.companies.map((c) => (
        <Button key={c.id} className="justify-start" disabled={switchCompany.isPending} onClick={() => switchCompany.mutate(c.id)}>
          {c.name}
        </Button>
      ))}
    </div>
  );
}

function CompanySwitcher({ me }: { me: Me }) {
  const switchCompany = useSwitchCompany();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button kind="ghost" size="sm">
          {t("top.switchCompany")}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 min-w-[220px] rounded-[8px] border border-rule bg-ground p-1.5 shadow-lg"
        >
          {me.companies.map((company) => (
            <DropdownMenu.Item
              key={company.id}
              disabled={company.id === me.active_company_id}
              onSelect={() => switchCompany.mutate(company.id)}
              className="flex cursor-pointer justify-between gap-4 rounded-[5px] px-2.5 py-1.5 text-sm outline-none data-[disabled]:cursor-default data-[disabled]:text-steel data-[highlighted]:bg-panel"
            >
              <span>{company.name}</span>
              <span className="text-xs text-steel">{t(`role.${company.role}` as MessageKey)}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function AccountMenu({ me }: { me: Me }) {
  const signOut = useSignOut();
  const switchCompany = useSwitchCompany();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(false);
  return (
    <>
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t("account.menu")}
        className="grid h-8 w-8 place-items-center rounded-full border border-rule bg-panel-2 text-xs font-medium"
      >
        {initials(me.display_name)}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[240px] rounded-[8px] border border-rule bg-ground p-1.5 shadow-lg"
        >
          <div className="px-2.5 py-1.5 text-xs text-steel">{t("account.signedInAs", { email: me.email })}</div>
          <DropdownMenu.Separator className="my-1 h-px bg-rule-soft" />
          {/* People look for other companies under their name too, not only beside the company name. */}
          {me.companies.length > 1 && (
            <>
              <DropdownMenu.Label className="px-2.5 pb-0.5 pt-1 text-[11px] uppercase tracking-[0.06em] text-steel">{t("account.companies")}</DropdownMenu.Label>
              {me.companies.map((company) => (
                <DropdownMenu.Item
                  key={company.id}
                  disabled={company.id === me.active_company_id}
                  onSelect={() => switchCompany.mutate(company.id)}
                  className="flex cursor-pointer items-center justify-between gap-4 rounded-[5px] px-2.5 py-1.5 text-sm outline-none data-[disabled]:cursor-default data-[highlighted]:bg-panel"
                >
                  <span className="flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full ${company.id === me.active_company_id ? "bg-blue" : "bg-transparent"}`} aria-hidden />
                    {company.name}
                  </span>
                  <span className="text-xs text-steel">{t(`role.${company.role}` as MessageKey)}</span>
                </DropdownMenu.Item>
              ))}
              <DropdownMenu.Separator className="my-1 h-px bg-rule-soft" />
            </>
          )}
          <DropdownMenu.Item
            onSelect={() => navigate("/account")}
            className="cursor-pointer rounded-[5px] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-panel"
          >
            {t("account.myAccount")}
          </DropdownMenu.Item>
          {me.operator && (
            <DropdownMenu.Item
              onSelect={() => navigate("/ops")}
              className="cursor-pointer rounded-[5px] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-panel"
            >
              {t("ops.menu")}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item
            onSelect={() => setFeedback(true)}
            className="cursor-pointer rounded-[5px] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-panel"
          >
            {t("feedback.menu")}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => navigate("/feedback")}
            className="cursor-pointer rounded-[5px] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-panel"
          >
            {t("feedback.page")}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => signOut.mutate()}
            className="cursor-pointer rounded-[5px] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-panel"
          >
            {t("account.signOut")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
    <FeedbackDialog open={feedback} onOpenChange={setFeedback} />
    </>
  );
}

/// An operator inside a company through support access (T-423): whose, why, until when, and the
/// way out.
function SupportBanner({ support }: { support: NonNullable<Me["support"]> }) {
  const leave = async () => {
    await api.post<void>("/ops/v1/support/leave");
    window.location.assign("/ops");
  };
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-warn/50 bg-warn/15 px-4 py-2 text-sm md:px-6" role="status">
      <span>
        {t("support.banner", { company: support.company, ticket: support.ticket, date: formatDate(support.expires_at) })}
        {!support.write && ` ${t("support.bannerReadOnly")}`}
      </span>
      <Button size="sm" onClick={() => void leave()}>{t("support.leave")}</Button>
    </div>
  );
}

function VerifyBanner() {
  const [sent, setSent] = useState(false);
  return (
    <div className="flex items-center gap-3 border-b border-rule-soft bg-panel px-6 py-2 text-sm">
      <span>{t("banner.verify")}</span>
      {sent ? (
        <span className="text-steel">{t("banner.resent")}</span>
      ) : (
        <Button
          kind="ghost"
          size="sm"
          onClick={() => {
            api.post("/auth/email/resend").finally(() => setSent(true));
          }}
        >
          {t("banner.resend")}
        </Button>
      )}
    </div>
  );
}

/// The "?" next to a screen's title: opens the matching help article in a new tab (UX-132).
export function HelpLink({ article }: { article: string }) {
  return (
    <a
      href={`/help/${article}`}
      target="_blank"
      rel="noreferrer"
      aria-label={t("help.forThisPage")}
      title={t("help.forThisPage")}
      className="inline-grid h-5 w-5 place-items-center rounded-full border border-rule text-[11px] font-medium text-steel no-underline hover:border-blue hover:text-blue"
    >
      ?
    </a>
  );
}

export function PageHeader({ title, help, children }: { title: string; help?: string; children?: React.ReactNode }) {
  return (
    // The actions move below the title when both do not fit on one line.
    <div className="flex min-h-[58px] flex-wrap items-center gap-x-3 gap-y-2 border-b border-rule-soft px-4 py-3 md:px-6">
      <h1 className="m-0 text-xl font-semibold">{title}</h1>
      {help && <HelpLink article={help} />}
      <span className="flex-1" />
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

/// The search box of the top bar (T-202): Enter opens the results.
function SearchBox() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  return (
    <form
      role="search"
      className="hidden min-w-0 max-w-[340px] flex-1 sm:block"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim().length >= 2) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      }}
    >
      <input
        type="search"
        aria-label={t("search.label")}
        placeholder={t("search.placeholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full rounded-[7px] border border-rule bg-ground px-3 py-1.5 text-sm"
      />
    </form>
  );
}
